import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Bell, Mail, Smartphone, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface NotificationSettingsData {
  email_notifications: {
    fraud_alerts: boolean
    daily_summary: boolean
    weekly_reports: boolean
    system_updates: boolean
  }
  push_notifications: {
    high_risk_leads: boolean
    pattern_detection: boolean
    threshold_breaches: boolean
  }
  sms_notifications: {
    critical_alerts: boolean
    phone_number: string
  }
  notification_frequency: string
  quiet_hours: {
    enabled: boolean
    start_time: string
    end_time: string
  }
}

export function NotificationSettings() {
  const [isLoading, setIsLoading] = useState(false)
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty }
  } = useForm<NotificationSettingsData>({
    defaultValues: {
      email_notifications: {
        fraud_alerts: true,
        daily_summary: true,
        weekly_reports: false,
        system_updates: true
      },
      push_notifications: {
        high_risk_leads: true,
        pattern_detection: true,
        threshold_breaches: false
      },
      sms_notifications: {
        critical_alerts: false,
        phone_number: ''
      },
      notification_frequency: 'immediate',
      quiet_hours: {
        enabled: false,
        start_time: '22:00',
        end_time: '08:00'
      }
    }
  })
  
  const emailNotifications = watch('email_notifications')
  const pushNotifications = watch('push_notifications')
  const smsNotifications = watch('sms_notifications')
  const quietHours = watch('quiet_hours')
  
  const onSubmit = async (data: NotificationSettingsData) => {
    setIsLoading(true)
    try {
      // Here would be the API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      
      toast.success('Notification settings updated')
    } catch (error) {
      toast.error('Failed to update settings')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Notification Settings
        </h2>
        <p className="text-sm text-muted-foreground">
          Configure how and when you receive notifications
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notifications
            </CardTitle>
            <CardDescription>
              Choose which email notifications you'd like to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Fraud Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Immediate alerts for high-risk fraud detection
                </p>
              </div>
              <Switch
                checked={emailNotifications.fraud_alerts}
                onCheckedChange={(checked) => 
                  setValue('email_notifications.fraud_alerts', checked, { shouldDirty: true })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Daily Summary</Label>
                <p className="text-sm text-muted-foreground">
                  Daily report of leads processed and blocked
                </p>
              </div>
              <Switch
                checked={emailNotifications.daily_summary}
                onCheckedChange={(checked) => 
                  setValue('email_notifications.daily_summary', checked, { shouldDirty: true })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Weekly Reports</Label>
                <p className="text-sm text-muted-foreground">
                  Comprehensive weekly analytics reports
                </p>
              </div>
              <Switch
                checked={emailNotifications.weekly_reports}
                onCheckedChange={(checked) => 
                  setValue('email_notifications.weekly_reports', checked, { shouldDirty: true })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">System Updates</Label>
                <p className="text-sm text-muted-foreground">
                  New features and system maintenance notifications
                </p>
              </div>
              <Switch
                checked={emailNotifications.system_updates}
                onCheckedChange={(checked) => 
                  setValue('email_notifications.system_updates', checked, { shouldDirty: true })
                }
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Push Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Push Notifications
            </CardTitle>
            <CardDescription>
              Browser and mobile push notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">High Risk Leads</Label>
                <p className="text-sm text-muted-foreground">
                  Instant alerts for leads above risk threshold
                </p>
              </div>
              <Switch
                checked={pushNotifications.high_risk_leads}
                onCheckedChange={(checked) => 
                  setValue('push_notifications.high_risk_leads', checked, { shouldDirty: true })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Pattern Detection</Label>
                <p className="text-sm text-muted-foreground">
                  Alerts when new fraud patterns are detected
                </p>
              </div>
              <Switch
                checked={pushNotifications.pattern_detection}
                onCheckedChange={(checked) => 
                  setValue('push_notifications.pattern_detection', checked, { shouldDirty: true })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Threshold Breaches</Label>
                <p className="text-sm text-muted-foreground">
                  Alerts when velocity thresholds are exceeded
                </p>
              </div>
              <Switch
                checked={pushNotifications.threshold_breaches}
                onCheckedChange={(checked) => 
                  setValue('push_notifications.threshold_breaches', checked, { shouldDirty: true })
                }
              />
            </div>
          </CardContent>
        </Card>
        
        {/* SMS Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              SMS Notifications
            </CardTitle>
            <CardDescription>
              Text message alerts for critical events
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Critical Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  SMS for urgent fraud alerts only
                </p>
              </div>
              <Switch
                checked={smsNotifications.critical_alerts}
                onCheckedChange={(checked) => 
                  setValue('sms_notifications.critical_alerts', checked, { shouldDirty: true })
                }
              />
            </div>
            
            {smsNotifications.critical_alerts && (
              <div className="space-y-2">
                <Label htmlFor="sms_phone">Phone Number</Label>
                <Input
                  id="sms_phone"
                  {...register('sms_notifications.phone_number')}
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
            <CardDescription>
              Control notification frequency and timing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Notification Frequency</Label>
              <Select
                value={watch('notification_frequency')}
                onValueChange={(value) => setValue('notification_frequency', value, { shouldDirty: true })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="hourly">Hourly Digest</SelectItem>
                  <SelectItem value="daily">Daily Digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Quiet Hours</Label>
                  <p className="text-sm text-muted-foreground">
                    Pause non-critical notifications during these hours
                  </p>
                </div>
                <Switch
                  checked={quietHours.enabled}
                  onCheckedChange={(checked) => 
                    setValue('quiet_hours.enabled', checked, { shouldDirty: true })
                  }
                />
              </div>
              
              {quietHours.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time</Label>
                    <Input
                      id="start_time"
                      {...register('quiet_hours.start_time')}
                      type="time"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time</Label>
                    <Input
                      id="end_time"
                      {...register('quiet_hours.end_time')}
                      type="time"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Actions */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!isDirty || isLoading}
          >
            {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />}
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  )
}